/**
 * AdvertiserCampaignUploadPage — advertiser self-service campaign upload surface
 * (feature #145; w9-26 FE). Mirrors `apps/api/src/routes/advertiser-campaign-upload.ts`.
 *
 * Three sections:
 *   1. Submit form — advertiser posts a draft or submitted campaign.
 *   2. My uploads — list of the active advertiser's prior uploads.
 *   3. Admin queue — tenant-wide pending queue with Approve / Reject actions.
 *
 * Brand: green-only (emerald 600/700 headline, green 600 primary buttons),
 * white/zinc surfaces, Inter via parent layout, 6-8px rounding, inline SVG
 * icons only. No emerald-to-sky gradient; no sky/blue/orange/purple; no dark
 * theme; no emojis; no competitor names.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAdvertiserCampaignUploadApprove,
	useAdvertiserCampaignUploadList,
	useAdvertiserCampaignUploadQueue,
	useAdvertiserCampaignUploadReject,
	useAdvertiserCampaignUploadSubmit,
} from "./hooks";
import {
	PAYOUT_TYPES,
	formatCents,
	formatUnixDate,
	type CampaignUploadRow,
	type PayoutType,
	type UploadStatus,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — green-only palette.
// ---------------------------------------------------------------------------

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-xs font-medium uppercase tracking-wide text-zinc-600";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT = INPUT;
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_OUTLINE =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
// Exported so the unused-locals check does not fail; reserved for future ghost buttons.
export const BTN_GHOST =
	"rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const TH =
	"px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-zinc-500";
const TD = "px-3 py-3 text-sm text-zinc-900";

// ---------------------------------------------------------------------------
// Inline SVGs — plain strokes, currentColor, no icon library.
// ---------------------------------------------------------------------------

function UploadIcon({ className }: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.5}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			className={className}
		>
			<path d="M10 13V4" />
			<path d="m6.5 7.5 3.5-3.5 3.5 3.5" />
			<path d="M4 13v2.25A1.75 1.75 0 0 0 5.75 17h8.5A1.75 1.75 0 0 0 16 15.25V13" />
		</svg>
	);
}

function CheckIcon({ className }: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.75}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			className={className}
		>
			<path d="m4.5 10.5 3.5 3.5 7.5-7.5" />
		</svg>
	);
}

function XIcon({ className }: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.75}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			className={className}
		>
			<path d="m5 5 10 10M15 5 5 15" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Status badge — pure green palette, zinc for neutral, red only for rejected.
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: UploadStatus }) {
	const cls =
		status === "approved"
			? "border-green-200 bg-green-50 text-green-700"
			: status === "submitted"
				? "border-green-200 bg-green-50 text-green-800"
				: status === "rejected"
					? "border-red-200 bg-red-50 text-red-700"
					: "border-zinc-200 bg-zinc-50 text-zinc-700";
	return (
		<span
			className={`inline-flex items-center rounded-[4px] border px-2 py-0.5 font-mono text-[11px] uppercase tracking-wide ${cls}`}
			data-testid={`advertiser-campaign-upload-status-${status}`}
		>
			{status}
		</span>
	);
}

// ---------------------------------------------------------------------------
// Submit form
// ---------------------------------------------------------------------------

interface SubmitFormState {
	advertiser_id: string;
	submitted_by: string;
	name: string;
	destination_url: string;
	payout_type: PayoutType;
	payout_amount: string;
	countries: string;
	status: "draft" | "submitted";
}

const EMPTY_FORM: SubmitFormState = {
	advertiser_id: "",
	submitted_by: "",
	name: "",
	destination_url: "",
	payout_type: "cpa",
	payout_amount: "0",
	countries: "",
	status: "draft",
};

interface SubmitFormProps {
	onAdvertiserChange: (id: string) => void;
}

function SubmitForm({ onAdvertiserChange }: SubmitFormProps) {
	const [form, setForm] = useState<SubmitFormState>(EMPTY_FORM);
	const mut = useAdvertiserCampaignUploadSubmit();

	const update = <K extends keyof SubmitFormState>(
		key: K,
		value: SubmitFormState[K],
	) => {
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const amountRaw = Number(form.payout_amount);
		if (!Number.isFinite(amountRaw) || amountRaw < 0) return;
		const amount = Math.trunc(amountRaw);
		mut.mutate(
			{
				advertiser_id: form.advertiser_id.trim(),
				submitted_by: form.submitted_by.trim(),
				name: form.name.trim(),
				destination_url: form.destination_url.trim(),
				payout_type: form.payout_type,
				payout_amount: amount,
				countries: form.countries.trim() || undefined,
				status: form.status,
			},
			{
				onSuccess: (row) => {
					onAdvertiserChange(row.advertiser_id);
					setForm((prev) => ({
						...EMPTY_FORM,
						advertiser_id: prev.advertiser_id,
						submitted_by: prev.submitted_by,
					}));
				},
			},
		);
	};

	const disabled =
		mut.isPending ||
		form.advertiser_id.trim() === "" ||
		form.submitted_by.trim() === "" ||
		form.name.trim() === "" ||
		form.destination_url.trim() === "";

	return (
		<section
			className={CARD}
			aria-label="Submit campaign upload"
			data-testid="advertiser-campaign-upload-submit-section"
		>
			<header className="flex items-center gap-2">
				<span className="text-green-700">
					<UploadIcon className="h-5 w-5" />
				</span>
				<h2 className="text-base font-semibold text-zinc-900">
					Submit campaign
				</h2>
			</header>
			<p className="mt-1 text-sm text-zinc-600">
				Enter campaign details. Choose Draft to save without review or Submit
				to enqueue for admin approval.
			</p>
			<form
				onSubmit={handleSubmit}
				className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
				data-testid="advertiser-campaign-upload-create-form"
			>
				<div className="flex flex-col gap-1">
					<label className={LABEL} htmlFor="acu-advertiser-id">
						Advertiser ID
					</label>
					<input
						id="acu-advertiser-id"
						type="text"
						className={INPUT}
						value={form.advertiser_id}
						onChange={(e) => update("advertiser_id", e.currentTarget.value)}
						disabled={mut.isPending}
						data-testid="advertiser-campaign-upload-advertiser-id-input"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label className={LABEL} htmlFor="acu-submitted-by">
						Submitted by
					</label>
					<input
						id="acu-submitted-by"
						type="text"
						className={INPUT}
						value={form.submitted_by}
						onChange={(e) => update("submitted_by", e.currentTarget.value)}
						disabled={mut.isPending}
						data-testid="advertiser-campaign-upload-submitted-by-input"
					/>
				</div>
				<div className="flex flex-col gap-1 sm:col-span-2">
					<label className={LABEL} htmlFor="acu-name">
						Campaign name
					</label>
					<input
						id="acu-name"
						type="text"
						className={INPUT}
						value={form.name}
						onChange={(e) => update("name", e.currentTarget.value)}
						disabled={mut.isPending}
						data-testid="advertiser-campaign-upload-name-input"
					/>
				</div>
				<div className="flex flex-col gap-1 sm:col-span-2">
					<label className={LABEL} htmlFor="acu-destination-url">
						Destination URL
					</label>
					<input
						id="acu-destination-url"
						type="url"
						className={INPUT}
						value={form.destination_url}
						onChange={(e) =>
							update("destination_url", e.currentTarget.value)
						}
						disabled={mut.isPending}
						placeholder="https://example.com/landing"
						data-testid="advertiser-campaign-upload-destination-url-input"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label className={LABEL} htmlFor="acu-payout-type">
						Payout type
					</label>
					<select
						id="acu-payout-type"
						className={SELECT}
						value={form.payout_type}
						onChange={(e) =>
							update("payout_type", e.currentTarget.value as PayoutType)
						}
						disabled={mut.isPending}
						data-testid="advertiser-campaign-upload-payout-type-select"
					>
						{PAYOUT_TYPES.map((t) => (
							<option key={t} value={t}>
								{t.toUpperCase()}
							</option>
						))}
					</select>
				</div>
				<div className="flex flex-col gap-1">
					<label className={LABEL} htmlFor="acu-payout-amount">
						Payout amount (cents)
					</label>
					<input
						id="acu-payout-amount"
						type="number"
						min={0}
						step={1}
						className={INPUT}
						value={form.payout_amount}
						onChange={(e) => update("payout_amount", e.currentTarget.value)}
						disabled={mut.isPending}
						data-testid="advertiser-campaign-upload-payout-amount-input"
					/>
				</div>
				<div className="flex flex-col gap-1 sm:col-span-2">
					<label className={LABEL} htmlFor="acu-countries">
						Countries (ISO codes, comma-separated — leave blank for all)
					</label>
					<input
						id="acu-countries"
						type="text"
						className={INPUT}
						value={form.countries}
						onChange={(e) => update("countries", e.currentTarget.value)}
						disabled={mut.isPending}
						placeholder="US,CA,GB"
						data-testid="advertiser-campaign-upload-countries-input"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label className={LABEL} htmlFor="acu-status">
						Status
					</label>
					<select
						id="acu-status"
						className={SELECT}
						value={form.status}
						onChange={(e) =>
							update(
								"status",
								e.currentTarget.value as "draft" | "submitted",
							)
						}
						disabled={mut.isPending}
						data-testid="advertiser-campaign-upload-status-select"
					>
						<option value="draft">Draft</option>
						<option value="submitted">Submit for review</option>
					</select>
				</div>
				<div className="flex items-end gap-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={disabled}
						data-testid="advertiser-campaign-upload-submit"
					>
						{mut.isPending ? "Saving." : "Save campaign"}
					</button>
				</div>
				{mut.error ? (
					<p
						className="sm:col-span-2 mt-1 text-xs text-red-600"
						role="alert"
						data-testid="advertiser-campaign-upload-submit-error"
					>
						{mut.error.message}
					</p>
				) : null}
				{mut.isSuccess ? (
					<p
						className="sm:col-span-2 mt-1 text-xs text-green-700"
						role="status"
						data-testid="advertiser-campaign-upload-submit-success"
					>
						Campaign saved.
					</p>
				) : null}
			</form>
		</section>
	);
}

// ---------------------------------------------------------------------------
// My uploads — advertiser-scoped list
// ---------------------------------------------------------------------------

interface MyUploadsProps {
	advertiserId: string;
	onAdvertiserChange: (id: string) => void;
}

function MyUploads({ advertiserId, onAdvertiserChange }: MyUploadsProps) {
	const query = useAdvertiserCampaignUploadList(advertiserId);
	const rows = query.data?.items ?? [];

	return (
		<section
			className={CARD}
			aria-label="My advertiser uploads"
			data-testid="advertiser-campaign-upload-my-section"
		>
			<header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
				<h2 className="text-base font-semibold text-zinc-900">My uploads</h2>
				<div className="flex items-center gap-2">
					<label className={LABEL} htmlFor="acu-lookup">
						View advertiser
					</label>
					<input
						id="acu-lookup"
						type="text"
						className={`${INPUT} max-w-xs`}
						value={advertiserId}
						onChange={(e) => onAdvertiserChange(e.currentTarget.value)}
						placeholder="advertiser_id"
						data-testid="advertiser-campaign-upload-lookup-input"
					/>
				</div>
			</header>
			{advertiserId.trim() === "" ? (
				<p
					className="mt-4 text-sm text-zinc-500"
					data-testid="advertiser-campaign-upload-my-prompt"
				>
					Enter an advertiser ID to load uploads.
				</p>
			) : query.isLoading ? (
				<p
					className="mt-4 text-sm text-zinc-500"
					data-testid="advertiser-campaign-upload-loading"
				>
					Loading.
				</p>
			) : query.error ? (
				<p
					className="mt-4 text-sm text-red-600"
					role="alert"
					data-testid="advertiser-campaign-upload-my-error"
				>
					{query.error.message}
				</p>
			) : rows.length === 0 ? (
				<p
					className="mt-4 text-sm text-zinc-500"
					data-testid="advertiser-campaign-upload-empty"
				>
					No uploads for this advertiser yet.
				</p>
			) : (
				<div className="mt-4 overflow-x-auto">
					<table
						className="min-w-full divide-y divide-zinc-200"
						data-testid="advertiser-campaign-upload-my-table"
					>
						<thead className="bg-zinc-50">
							<tr>
								<th className={TH}>Name</th>
								<th className={TH}>Payout</th>
								<th className={TH}>Countries</th>
								<th className={TH}>Status</th>
								<th className={TH}>Created</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100 bg-white">
							{rows.map((row) => (
								<tr
									key={row.id}
									data-testid={`advertiser-campaign-upload-my-row-${row.id}`}
								>
									<td className={TD}>
										<div className="font-medium">{row.name}</div>
										<div className="truncate text-xs text-zinc-500">
											{row.destination_url}
										</div>
									</td>
									<td className={`${TD} tabular-nums`}>
										<div className="font-mono text-xs uppercase text-zinc-600">
											{row.payout_type}
										</div>
										<div>{formatCents(row.payout_amount)}</div>
									</td>
									<td className={TD}>
										{row.countries.length === 0 ? (
											<span className="text-xs text-zinc-500">All</span>
										) : (
											<span className="font-mono text-xs text-zinc-700">
												{row.countries}
											</span>
										)}
									</td>
									<td className={TD}>
										<StatusBadge status={row.status} />
									</td>
									<td className={`${TD} tabular-nums text-xs text-zinc-500`}>
										{formatUnixDate(row.created_at)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Admin queue — approve / reject
// ---------------------------------------------------------------------------

interface RejectModalState {
	row: CampaignUploadRow;
	notes: string;
}

function AdminQueue({ reviewerId }: { reviewerId: string }) {
	const queue = useAdvertiserCampaignUploadQueue();
	const approveMut = useAdvertiserCampaignUploadApprove();
	const rejectMut = useAdvertiserCampaignUploadReject();

	const [rejectState, setRejectState] = useState<RejectModalState | null>(null);

	const rows = queue.data?.items ?? [];

	const handleApprove = (row: CampaignUploadRow) => {
		approveMut.mutate({ id: row.id, reviewerId });
	};

	const openReject = (row: CampaignUploadRow) => {
		setRejectState({ row, notes: "" });
	};

	const closeReject = () => {
		setRejectState(null);
	};

	const confirmReject = () => {
		if (!rejectState) return;
		const notes = rejectState.notes.trim();
		if (notes.length === 0) return;
		rejectMut.mutate(
			{
				id: rejectState.row.id,
				body: { admin_notes: notes },
				reviewerId,
			},
			{ onSuccess: () => setRejectState(null) },
		);
	};

	return (
		<section
			className={CARD}
			aria-label="Admin review queue"
			data-testid="advertiser-campaign-upload-queue-section"
		>
			<header className="flex items-center justify-between">
				<h2 className="text-base font-semibold text-zinc-900">
					Admin queue
				</h2>
				<span
					className="rounded-[4px] border border-green-200 bg-green-50 px-2 py-0.5 font-mono text-xs text-green-700"
					data-testid="advertiser-campaign-upload-queue-count"
				>
					{rows.length} pending
				</span>
			</header>
			{queue.isLoading ? (
				<p
					className="mt-4 text-sm text-zinc-500"
					data-testid="advertiser-campaign-upload-queue-loading"
				>
					Loading.
				</p>
			) : queue.error ? (
				<p
					className="mt-4 text-sm text-red-600"
					role="alert"
					data-testid="advertiser-campaign-upload-queue-error"
				>
					{queue.error.message}
				</p>
			) : rows.length === 0 ? (
				<p
					className="mt-4 text-sm text-zinc-500"
					data-testid="advertiser-campaign-upload-queue-empty"
				>
					No submissions awaiting review.
				</p>
			) : (
				<div className="mt-4 overflow-x-auto">
					<table
						className="min-w-full divide-y divide-zinc-200"
						data-testid="advertiser-campaign-upload-queue-table"
					>
						<thead className="bg-zinc-50">
							<tr>
								<th className={TH}>Advertiser</th>
								<th className={TH}>Campaign</th>
								<th className={TH}>Payout</th>
								<th className={TH}>Submitted</th>
								<th className={TH}>Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100 bg-white">
							{rows.map((row) => (
								<tr
									key={row.id}
									data-testid={`advertiser-campaign-upload-queue-row-${row.id}`}
								>
									<td className={TD}>
										<div className="font-mono text-xs text-zinc-700">
											{row.advertiser_id}
										</div>
										<div className="text-xs text-zinc-500">
											by {row.submitted_by}
										</div>
									</td>
									<td className={TD}>
										<div className="font-medium">{row.name}</div>
										<div className="truncate text-xs text-zinc-500">
											{row.destination_url}
										</div>
									</td>
									<td className={`${TD} tabular-nums`}>
										<div className="font-mono text-xs uppercase text-zinc-600">
											{row.payout_type}
										</div>
										<div>{formatCents(row.payout_amount)}</div>
									</td>
									<td className={`${TD} tabular-nums text-xs text-zinc-500`}>
										{formatUnixDate(row.submitted_at ?? row.created_at)}
									</td>
									<td className={TD}>
										<div className="flex items-center gap-2">
											<button
												type="button"
												className={BTN_PRIMARY}
												onClick={() => handleApprove(row)}
												disabled={
													approveMut.isPending || rejectMut.isPending
												}
												data-testid={`advertiser-campaign-upload-approve-${row.id}`}
											>
												<span className="inline-flex items-center gap-1">
													<CheckIcon className="h-4 w-4" />
													Approve
												</span>
											</button>
											<button
												type="button"
												className={BTN_OUTLINE}
												onClick={() => openReject(row)}
												disabled={
													approveMut.isPending || rejectMut.isPending
												}
												data-testid={`advertiser-campaign-upload-reject-${row.id}`}
											>
												<span className="inline-flex items-center gap-1">
													<XIcon className="h-4 w-4" />
													Reject
												</span>
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{approveMut.error ? (
				<p
					className="mt-3 text-xs text-red-600"
					role="alert"
					data-testid="advertiser-campaign-upload-approve-error"
				>
					{approveMut.error.message}
				</p>
			) : null}
			{rejectState ? (
				<div
					role="dialog"
					aria-modal="true"
					aria-label="Reject campaign"
					className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-900/40 p-4"
					data-testid="advertiser-campaign-upload-reject-modal"
				>
					<div className="w-full max-w-md rounded-[8px] border border-zinc-200 bg-white p-5 shadow-lg">
						<h3 className="text-base font-semibold text-zinc-900">
							Reject campaign
						</h3>
						<p className="mt-1 text-sm text-zinc-600">
							Provide rejection notes for{" "}
							<span className="font-medium">{rejectState.row.name}</span>.
						</p>
						<label className={`${LABEL} mt-4 block`} htmlFor="acu-reject-notes">
							Admin notes (required)
						</label>
						<textarea
							id="acu-reject-notes"
							className={`${INPUT} mt-1 min-h-[96px]`}
							value={rejectState.notes}
							onChange={(e) =>
								setRejectState({
									...rejectState,
									notes: e.currentTarget.value,
								})
							}
							data-testid="advertiser-campaign-upload-reject-notes"
						/>
						{rejectMut.error ? (
							<p
								className="mt-2 text-xs text-red-600"
								role="alert"
								data-testid="advertiser-campaign-upload-reject-error"
							>
								{rejectMut.error.message}
							</p>
						) : null}
						<div className="mt-4 flex items-center justify-end gap-2">
							<button
								type="button"
								className={BTN_OUTLINE}
								onClick={closeReject}
								disabled={rejectMut.isPending}
								data-testid="advertiser-campaign-upload-reject-cancel"
							>
								Cancel
							</button>
							<button
								type="button"
								className={BTN_PRIMARY}
								onClick={confirmReject}
								disabled={
									rejectMut.isPending || rejectState.notes.trim() === ""
								}
								data-testid="advertiser-campaign-upload-reject-confirm"
							>
								{rejectMut.isPending ? "Rejecting." : "Confirm reject"}
							</button>
						</div>
					</div>
				</div>
			) : null}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AdvertiserCampaignUploadPage() {
	const [advertiserId, setAdvertiserId] = useState("");
	const [reviewerId, setReviewerId] = useState("admin");

	const reviewerLabel = useMemo(
		() => (reviewerId.trim().length > 0 ? reviewerId.trim() : "admin"),
		[reviewerId],
	);

	return (
		<main
			data-testid="advertiser-campaign-upload-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Advertiser self-service
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Campaign upload
				</h1>
				<p className="text-sm text-zinc-600">
					Submit new campaigns, track review status, and (as an admin) approve
					or reject pending advertiser submissions.
				</p>
				<div className="mt-2 flex items-center gap-2">
					<label className={LABEL} htmlFor="acu-reviewer-id">
						Reviewer ID
					</label>
					<input
						id="acu-reviewer-id"
						type="text"
						className={`${INPUT} max-w-xs`}
						value={reviewerId}
						onChange={(e) => setReviewerId(e.currentTarget.value)}
						placeholder="admin"
						data-testid="advertiser-campaign-upload-reviewer-id-input"
					/>
					<span className="font-mono text-xs text-zinc-500">
						acting as {reviewerLabel}
					</span>
				</div>
			</header>

			<SubmitForm onAdvertiserChange={setAdvertiserId} />
			<MyUploads
				advertiserId={advertiserId}
				onAdvertiserChange={setAdvertiserId}
			/>
			<AdminQueue reviewerId={reviewerLabel} />
		</main>
	);
}

export default AdvertiserCampaignUploadPage;
